import React from 'react'

import { connect } from 'react-redux'
import { matchPath, useLocation } from 'react-router-dom'

import { useCompactNavbarVisible } from '@hooks'

import { SearchableCompanyMenu } from '@components/navigations/elements/ProfileMenu/SearchableCompanyMenu'
import { Popover, Typography } from '@components/ui'
import { SettingsIcon } from '@components/ui/svgIcons'

import { ROUTES } from '@constants'

import { CompanyNameWithBadge } from '../CompanyNameWithBadge'

import { TertiaryInfoTypography } from '../styles'
import { MyCompaniesMessage, ProfileSettingsMessage } from './messages'
import {
  MenuFooter,
  MenuHeader,
  MenuRootContainer,
  MenuRootMobileContainer,
  ProfileDesktopNavLink,
  RootContainer,
  StyledKeyboardArrowDownIcon,
  StyledNavButton,
  StyledNavLink,
} from './styles'

interface MenuProps {
  companies: CompaniesItem[]
  currentCompanyId: number | undefined
  disabled: boolean
  isCompact?: boolean
  userEmail: string
  handleDialogOpen: VoidFunction
  handleSelectCompany: (company: CompaniesItem, isSettings?: boolean) => (event: React.MouseEvent<HTMLElement>) => void
  handleClose: (event: React.MouseEvent<HTMLElement> | MouseEvent | TouchEvent) => void
}

function Menu({
  companies,
  currentCompanyId,
  disabled,
  isCompact,
  userEmail,
  handleSelectCompany,
  handleClose,
}: MenuProps) {
  return (
    <>
      <MenuHeader>
        <Typography color="gray-80" size="700-md">
          {MyCompaniesMessage}
        </Typography>
      </MenuHeader>
      <SearchableCompanyMenu
        currentCompanyId={currentCompanyId}
        data={companies}
        disabled={disabled}
        onSelect={handleSelectCompany}
      />
      {!isCompact && (
        <MenuFooter>
          <ProfileDesktopNavLink linkWhenNoCompany to={ROUTES.profile} onClick={handleClose}>
            <SettingsIcon />
            <Typography size="700-sm">{ProfileSettingsMessage}</Typography>
            <TertiaryInfoTypography>{userEmail}</TertiaryInfoTypography>
          </ProfileDesktopNavLink>
        </MenuFooter>
      )}
    </>
  )
}

interface ProfileMenuProps extends Omit<MenuProps, 'handleDialogOpen' | 'handleSelectCompany' | 'handleClose'> {
  isActive?: boolean
  isDesktop?: boolean
  onCloseCallback?: VoidFunction
}

function PureProfileMenu({
  companies,
  currentCompanyId,
  disabled,
  isActive = false,
  isCompact = false,
  isDesktop = false,
  onCloseCallback,
  userEmail,
}: ProfileMenuProps) {
  const location = useLocation()
  const compactVisible = useCompactNavbarVisible()

  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false)
  const popoverContainerRef = React.useRef<HTMLDivElement>(null)

  // Hide popover when compact navbar is visible
  React.useEffect(() => {
    if (compactVisible && isPopoverOpen) {
      setIsPopoverOpen(false)
    }
  }, [compactVisible, isPopoverOpen])

  function handleClose(event: React.MouseEvent<HTMLElement> | MouseEvent | TouchEvent) {
    // Support both React and native events
    if (popoverContainerRef.current?.contains(event.target as Nullable<Node>)) {
      return
    }
    setIsPopoverOpen(false)
    onCloseCallback?.()
  }

  function handleSelectCompany(company: CompaniesItem, isSettings?: boolean) {
    return function handler(event: React.MouseEvent<HTMLElement>) {
      const matchSettings = matchPath(ROUTES.company, location.pathname)
      if (company.id === currentCompanyId) {
        if (!isSettings || (isSettings && matchSettings && matchSettings.params.company_id === String(company.id))) {
          event.preventDefault()
        }
      }
      setIsPopoverOpen(false)
      onCloseCallback?.()
    }
  }

  function handleDialogOpen() {
    setIsPopoverOpen(false)
    onCloseCallback?.()
  }

  // DESKTOP
  if (isDesktop) {
    if (companies.length) {
      return (
        <RootContainer>
          <Popover
            open={isPopoverOpen}
            onOpenChange={setIsPopoverOpen}
            shift={{ padding: 6 }}
            withoutPortal
            withoutScrollLock
            trigger={
              <StyledNavButton $isActive={isActive} data-testid="profile-menu-btn" disabled={disabled}>
                <CompanyNameWithBadge />
                <StyledKeyboardArrowDownIcon size={20} />
              </StyledNavButton>
            }
          >
            {/* id is used by e2e tests, do not remove it */}
            <MenuRootContainer ref={popoverContainerRef} data-testid="profile-menu" id="menu-list-grow">
              <Menu
                companies={companies}
                currentCompanyId={currentCompanyId}
                disabled={disabled}
                isCompact={isCompact}
                userEmail={userEmail}
                handleDialogOpen={handleDialogOpen}
                handleSelectCompany={handleSelectCompany}
                handleClose={handleClose}
              />
            </MenuRootContainer>
          </Popover>
        </RootContainer>
      )
    } else {
      // user has no company
      return (
        <RootContainer>
          <StyledNavLink linkWhenNoCompany to={ROUTES.profile} $isActive={isActive}>
            {ProfileSettingsMessage}
          </StyledNavLink>
        </RootContainer>
      )
    }
  }

  // MOBILE
  if (companies.length) {
    return (
      <RootContainer>
        <MenuRootMobileContainer data-testid="profile-menu">
          <Menu
            companies={companies}
            currentCompanyId={currentCompanyId}
            disabled={disabled}
            isCompact={isCompact}
            userEmail={userEmail}
            handleDialogOpen={handleDialogOpen}
            handleSelectCompany={handleSelectCompany}
            handleClose={handleClose}
          />
        </MenuRootMobileContainer>
      </RootContainer>
    )
  } else {
    // user has no company
    return (
      <RootContainer>
        <StyledNavLink linkWhenNoCompany to={ROUTES.profile} $isActive={isActive}>
          {ProfileSettingsMessage}
        </StyledNavLink>
      </RootContainer>
    )
  }
}

export const ProfileMenu = connect((state: Store) => ({
  companies: state.auth.companies,
  currentCompanyId: state.auth.company.data?.id,
  disabled: state.auth.company.loading,
  userEmail: state.auth.user.email,
}))(PureProfileMenu)

ProfileMenu.displayName = 'ProfileMenu'
